import Toggle from "@/components/ui/toggle";
import type { FlowSection } from "./types";

// Labels for sections based on type (always used, ignoring section title)
const SECTION_TYPE_LABELS: Record<string, string> = {
	rating: "Rating",
	welcome: "Welcome",
	"personal-detail": "Personal details",
	"company-detail": "Company details",
	consent: "Consent",
	"thank-you": "Thank you",
	"negative-feedback": "Feedback",
	"feedback-thank-you": "Feedback Thank you",
};

interface FlowItemProps {
	section: FlowSection;
	isSelected: boolean;
	onSelect: () => void;
	onToggle?: () => void;
	disabled?: boolean;
}

export default function FlowItem({ section, isSelected, onSelect, onToggle, disabled = false }: FlowItemProps) {
	// Always use type-based label, ignoring section title
	const displayTitle = SECTION_TYPE_LABELS[section.type] || section.type;
	const handleToggleClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		onToggle?.();
	};

	// Disabled sections (toggled off) should not be selectable
	const isSelectable = section.enabled && !disabled;

	const handleClick = () => {
		if (isSelectable) {
			onSelect();
		}
	};

	// Determine circle styles based on state
	const getCircleStyles = () => {
		if (isSelected) {
			return "bg-primary ring-4 ring-primary/20";
		}
		if (!section.enabled) {
			return "border-2 border-gray-300 bg-white";
		}
		return "bg-gray-400";
	};

	return (
		<div onClick={handleClick} className="flex items-center gap-3 relative">
			{/* Step indicator circle */}
			<div className={`w-3 h-3 rounded-full shrink-0 z-10 ${getCircleStyles()}`} />

			{/* Content */}
			<div
				className={`flex-1 flex items-center justify-between py-2 px-3 rounded-lg transition-colors ${
					isSelected ? "bg-primary/5" : isSelectable ? "hover:bg-gray-50 cursor-pointer" : ""
				} ${!section.enabled ? "opacity-60" : ""} ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
			>
				<span
					className={`text-sm font-medium ${
						isSelected ? "text-primary" : section.enabled ? "text-primary" : "text-tertiary"
					}`}
				>
					{displayTitle}
				</span>

				{section.canToggle && onToggle && (
					<div onClick={handleToggleClick}>
						<Toggle checked={section.enabled} onChange={onToggle} disabled={disabled} />
					</div>
				)}
			</div>
		</div>
	);
}
